import { ChordsRow, CustomChordVariation } from "../Entities/SongModel";
import { chordLibrary, normalizeChord } from "../Components/ChordViewer";

export const CHORD_REGEX =
  /^[A-Ga-g][b#♯♭]?(?:maj|min|m|M|dim|aug|sus|add|\+|°|ø|Δ|-|[0-9]|b5|#5|b9|#9|#11|b13)*(?:\/(?:[0-9\+#b♯♭\-a-zA-Z]+))?(?:\/[A-Ga-g][b#♯♭]?(?:m|min|maj)?)?$/;

export function isChordToken(token: string, customChords?: Record<string, CustomChordVariation>): boolean {
  const clean = token.replace(/[()[\],]/g, "").trim();
  if (!clean) return false;
  if (customChords && (customChords[clean] || customChords[normalizeChord(clean)])) {
    return true;
  }
  if (chordLibrary[normalizeChord(clean)]) {
    return true;
  }
  return CHORD_REGEX.test(clean);
}

export function isChordLine(line: string, customChords?: Record<string, CustomChordVariation>): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (/[\u0590-\u05FF]/.test(trimmed)) {
    return false;
  }
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (!tokens.length) return false;

  let chordCount = 0;
  for (const token of tokens) {
    if (isChordToken(token, customChords)) {
      chordCount++;
    }
  }

  return chordCount / tokens.length >= 0.5;
}

export function isHeaderLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) return true;
  if (trimmed.startsWith("(") && trimmed.endsWith(")")) return true;
  const lower = trimmed.toLowerCase();
  const keywords = [
    "verse", "chorus", "bridge", "intro", "outro", "solo", "hook",
    "בית", "פזמון", "גשר", "פתיחה", "סיום", "מעבר", "הקדמה", "סולו"
  ];
  return keywords.some((kw) => lower.startsWith(kw) || lower.endsWith(":"));
}

export function isTabLine(line: string): boolean {
  const trimmed = line.trim();
  return /^[a-gA-GeE]\|[-0-9\s/\\bph~]+/.test(trimmed) || /^\|[-0-9\s/\\bph~]+\|/.test(trimmed);
}

export function parseTextToBody(
  rawText: string,
  customChords?: Record<string, CustomChordVariation>
): ChordsRow[] {
  if (!rawText.trim()) return [];

  const rawLines = rawText.split("\n");
  const rows: ChordsRow[] = [];
  let i = 0;

  while (i < rawLines.length) {
    const line = rawLines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i++;
      continue;
    }

    if (isHeaderLine(trimmed)) {
      rows.push({ header: trimmed });
      i++;
      continue;
    }

    if (isTabLine(trimmed)) {
      const tabLines: string[] = [line];
      i++;
      while (i < rawLines.length && isTabLine(rawLines[i].trim())) {
        tabLines.push(rawLines[i]);
        i++;
      }
      rows.push({ tab: tabLines });
      continue;
    }

    if (isChordLine(trimmed, customChords)) {
      const chords = line;
      let text: string | undefined = undefined;

      if (i + 1 < rawLines.length) {
        const nextLine = rawLines[i + 1];
        const nextTrimmed = nextLine.trim();
        if (
          nextTrimmed &&
          !isHeaderLine(nextTrimmed) &&
          !isTabLine(nextTrimmed) &&
          !isChordLine(nextTrimmed, customChords)
        ) {
          text = nextLine;
          i += 2;
          rows.push({ chords, text });
          continue;
        }
      }

      rows.push({ chords });
      i++;
      continue;
    }

    // Regular lyrics line
    rows.push({ text: line });
    i++;
  }

  return rows;
}

export function bodyToText(body: ChordsRow[]): string {
  if (!body || !body.length) return "";

  const blocks: string[] = [];
  for (const row of body) {
    const lines: string[] = [];
    if (row.header) lines.push(row.header);
    if (row.chords) lines.push(row.chords);
    if (row.text) lines.push(row.text);
    if (row.tab && row.tab.length) lines.push(row.tab.join("\n"));
    if (lines.length) blocks.push(lines.join("\n"));
  }

  return blocks.join("\n\n");
}
